import type { SelectedFile, SelectedPage } from '@content-selector/stores/app'

import type { Selection } from '@lib/types/selections'
import type { FileRetrieve } from '@openapi'
import type { ContentGridProps } from '@canvas-builder/types/canvas'
import type {
  CollectionFile,
  CollectionPage,
  CollectionSlide,
  CollectionGroup,
  CollectionPlayerV2,
  CollectionSlideV2,
} from '@collection-player/types/collection-player.type'
import { getUniqueSlides } from '@collection-player/utils/node.util'
import {
  generateNewCollectionFile,
  generateNewCollectionSlide,
  generateNewEmptyGroup,
  getDefaultCollectionName,
  getDefaultGroupName,
} from '@collection-selector/utils/data.util'
import type { I18nTranslate } from '@lib/types/common'

export function parsePptxFileToCollectionPlayer(t: I18nTranslate, _file: FileRetrieve) {
  const metadataSections =
    (_file?.metadata?.pptx_data?.sections as { start: number; end: number; name: string | null }[]) ?? []
  const sections =
    metadataSections.length || _file.content_thumbnails.length === 0
      ? metadataSections
      : [{ start: 0, end: _file.content_thumbnails.length - 1, name: null }]

  const sectionGroups = sections.map((section) => ({
    name: section.name ?? (_file.name || getDefaultGroupName(t)),
    slides: _file.content_thumbnails
      .slice(section.start, section.end + 1)
      .map((url, index) => ({ url, index: section.start + index })),
  }))

  const file = {
    id: _file.id,
    pspdfkit_auth_payload: _file.pspdfkit_auth_payload,
    pspdfkit_document_id: _file.pspdfkit_document_id,
    pspdfkit_server_url: _file.pspdfkit_server_url,
    content_type: _file.content_type,
    content_url: _file.content_url,
    permissions: _file.permissions,
    thumbnail_url: _file.thumbnail_url ?? null,
    name: _file.name,
    type: _file.type,
    metadata: _file.metadata,
  }

  return {
    name: file.name ?? getDefaultGroupName(t),
    groups: sectionGroups.map((section) => ({
      ...generateNewEmptyGroup(t),
      name: section.name,
      slides: section.slides.map((slide) =>
        generateNewCollectionSlide({
          file,
          slide,
        }),
      ),
    })),
  }
}

export function parsePdfFileToCollectionPlayer(t: I18nTranslate, file: FileRetrieve) {
  const slides = file.content_thumbnails.map((url, index) =>
    generateNewCollectionSlide({
      file: {
        id: file.id,
        pspdfkit_auth_payload: file.pspdfkit_auth_payload,
        pspdfkit_document_id: file.pspdfkit_document_id,
        pspdfkit_server_url: file.pspdfkit_server_url,
        content_type: file.content_type,
        content_url: file.content_url,
        permissions: file.permissions,
        thumbnail_url: file.thumbnail_url ?? null,
        name: file.name,
        type: file.type,
        metadata: file.metadata,
      },
      slide: {
        index,
        url,
      },
    }),
  )

  return {
    name: file.name ?? getDefaultCollectionName(t),
    groups: [
      {
        ...generateNewEmptyGroup(t),
        name: file.name ?? getDefaultGroupName(t),
        slides,
      },
    ],
  }
}

export function parseFileToCollectionPlayer(t: I18nTranslate, file: FileRetrieve) {
  if (file.content_type === 'pdf') {
    if (file.original_extension === 'pptx' || file.metadata?.pptx_data?.sections?.length) {
      return parsePptxFileToCollectionPlayer(t, file)
    }

    return parsePdfFileToCollectionPlayer(t, file)
  }

  return {
    name: file.name ?? getDefaultCollectionName(t),
    groups: [
      {
        ...generateNewEmptyGroup(t),
        name: file.name ?? getDefaultGroupName(t),
        slides: [
          generateNewCollectionFile({
            id: file.id,
            pspdfkit_auth_payload: file.pspdfkit_auth_payload,
            pspdfkit_document_id: file.pspdfkit_document_id,
            pspdfkit_server_url: file.pspdfkit_server_url,
            content_type: file.content_type,
            content_url: file.content_url,
            permissions: file.permissions,
            thumbnail_url: file.thumbnail_url ?? null,
            name: file.name,
            type: file.type,
            metadata: file.metadata,
          }),
        ],
      },
    ],
  }
}

export function parseContentSelectorToCollectionPlayerSlides({
  selectedItems,
  filesById,
}: {
  selectedItems: (SelectedFile | SelectedPage)[]
  filesById: Record<FileRetrieve['id'], FileRetrieve>
}): (CollectionPage | CollectionFile)[] {
  return selectedItems.reduce((acc, item): CollectionSlide[] => {
    if (item.type === 'file') {
      if (filesById[item.id].content_type === 'pdf') {
        const pages: CollectionPage[] = filesById[item.id].content_thumbnails.map((contentThumbnail, index) =>
          generateNewCollectionSlide({
            file: {
              id: item.id,
              pspdfkit_auth_payload: filesById[item.id].pspdfkit_auth_payload,
              pspdfkit_document_id: filesById[item.id].pspdfkit_document_id,
              pspdfkit_server_url: filesById[item.id].pspdfkit_server_url,
              content_type: filesById[item.id].content_type,
              content_url: filesById[item.id].content_url,
              permissions: item.permissions,
              thumbnail_url: item.thumbnail_url ?? null,
              name: item.name,
              type: filesById[item.id].type,
              metadata: item.metadata,
            },
            slide: {
              index,
              url: contentThumbnail,
            },
          }),
        )

        return getUniqueSlides(acc, pages)
      }

      return [
        ...acc,
        generateNewCollectionFile({
          id: item.id,
          pspdfkit_auth_payload: filesById[item.id].pspdfkit_auth_payload,
          pspdfkit_document_id: filesById[item.id].pspdfkit_document_id,
          pspdfkit_server_url: filesById[item.id].pspdfkit_server_url,
          content_type: filesById[item.id].content_type,
          content_url: filesById[item.id].content_url,
          permissions: item.permissions,
          thumbnail_url: filesById[item.id].thumbnail_url,
          name: item.name,
          type: filesById[item.id].type,
          metadata: item.metadata,
        }),
      ]
    }

    return getUniqueSlides(acc, [
      generateNewCollectionSlide({
        file: {
          id: item.file.id,
          pspdfkit_auth_payload: filesById[item.file.id].pspdfkit_auth_payload,
          pspdfkit_document_id: filesById[item.file.id].pspdfkit_document_id,
          pspdfkit_server_url: filesById[item.file.id].pspdfkit_server_url,
          content_type: filesById[item.file.id].content_type,
          content_url: filesById[item.file.id].content_url,
          permissions: item.permissions,
          thumbnail_url: item.thumbnail_url ?? null,
          name: item.file.name,
          type: filesById[item.file.id].type,
          metadata: filesById[item.file.id].metadata,
        },
        slide: {
          index: item.page_index,
          url: item.thumbnail_url ?? null,
        },
      }),
    ])
  }, [] as CollectionSlide[])
}

export function parseCollectionPlayerSlidesToContentSelector(
  slides?: CollectionGroup['slides'] | CollectionSlideV2[],
): Selection[] {
  if (!slides?.length) return []

  return slides.reduce((acc, item) => {
    let selection: Selection | null = null

    // Check if this is V2 format (has file_id directly)
    if ('file_id' in item) {
      // V2 format: CollectionSlideV2
      const v2Item = item as CollectionSlideV2

      if (!v2Item.file_id) {
        return acc // Skip slides without file ID
      }

      if (typeof v2Item.index === 'number') {
        // Has index = page selection
        selection = {
          fileId: v2Item.file_id,
          type: 'page',
          pageIndex: v2Item.index,
        }
      } else {
        // No index = file selection
        selection = {
          fileId: v2Item.file_id,
          type: 'file',
        }
      }
    } else {
      // V1 format: CollectionSlide (CollectionPage | CollectionFile)
      const v1Item = item as CollectionSlide
      const fileId = v1Item.file?.id

      if (!fileId) {
        return acc // Skip slides without file ID
      }

      if (v1Item.type === 'slide') {
        selection = {
          fileId,
          type: 'page',
          pageIndex: v1Item.slide.index,
        }
      } else {
        selection = {
          fileId,
          type: 'file',
        }
      }
    }

    return [...acc, selection]
  }, [] as Selection[])
}

/**
 * Transform an array of file data into CollectionPlayerV2 format
 * Returns minimal data structure with only file_id references
 * Each file gets its own group to maintain proper structure for PPTX sections
 * @param t - Translation function
 * @param filesData - Array of file data with id, name, and optional selectedPages
 * @param name - Name for the collection (defaults to 'Collection')
 * @returns Transformed CollectionPlayerV2 data with file_id references
 */
export function transformFilesToCollectionPlayer(
  t: I18nTranslate,
  filesData: any[],
  name = 'Collection',
): CollectionPlayerV2 {
  if (!Array.isArray(filesData)) {
    throw new Error('Expected an array of files')
  }

  // Transform each file data item into its own group
  const groups = filesData.reduce((acc: CollectionPlayerV2['groups'], fileData, index) => {
    try {
      const fileId = fileData.id
      const fileName = fileData.name || `File ${index + 1}`
      const selectedPages = fileData.selectedPages

      if (!fileId) {
        return acc // Skip if no file ID
      }

      let slidesToCreate: CollectionSlideV2[] = []

      if (selectedPages && Array.isArray(selectedPages) && selectedPages.length > 0) {
        // Create slides only for specified pages
        slidesToCreate = selectedPages.map((pageIndex) => ({
          id: `${fileId}-slide-${pageIndex}`,
          file_id: fileId,
          index: pageIndex,
        }))
      } else {
        // No selectedPages specified - create a single slide representing the entire file
        // The resolve API will determine if it's a PDF with multiple pages or a single file
        // Using undefined index to indicate "entire file" rather than a specific page
        slidesToCreate = [
          {
            id: `${fileId}-file`,
            file_id: fileId,
            index: undefined, // undefined means entire file, not a specific page
          },
        ]
      }

      // Create a separate group for each file
      acc.push({
        id: `group-${fileId}`,
        name: fileName,
        slides: slidesToCreate,
      })

      return acc
    } catch (error) {
      // Skip invalid files
      return acc
    }
  }, [])

  return {
    name,
    groups,
  }
}

/**
 * Transform an array of file data into ContentGrid format
 * Handles the new data structure with file references and selectedPages arrays
 * @param filesData - Array of file data with id, name, and optional selectedPages
 * @returns Transformed ContentGridProps data
 */
export function transformFilesToContentGrid(filesData: any[]): ContentGridProps {
  if (!Array.isArray(filesData)) {
    throw new Error('Expected an array of files')
  }

  // Transform each file data into ContentGrid items
  const items = filesData.reduce((acc: ContentGridProps['items'], fileData) => {
    try {
      const fileId = fileData.id
      const selectedPages = fileData.selectedPages

      if (!fileId) {
        return acc // Skip if no file ID
      }

      let itemsToCreate: ContentGridProps['items'] = []

      if (selectedPages && Array.isArray(selectedPages) && selectedPages.length > 0) {
        // Create items only for specified pages
        // For ContentGrid, each selected page becomes a slide item
        itemsToCreate = selectedPages.map((pageIndex) => ({
          file: {
            id: fileId,
            // ContentGrid will resolve these properties via data accessor or file resolution
            name: fileData.name || `File ${fileId}`,
            permissions: undefined, // Will be resolved
            tags: undefined, // Will be resolved
            content_url: undefined, // Will be resolved
            content_type: undefined, // Will be resolved
            content_extension: undefined, // Will be resolved
            expires_at: undefined, // Will be resolved
          },
          slide: {
            index: pageIndex,
            url: undefined, // Will be resolved from content_thumbnails
          },
          type: 'slide' as const,
        }))
      } else {
        // No selectedPages specified - create a single item representing the entire file
        // The ContentGrid component will determine if it's a PDF with multiple pages or a single file
        itemsToCreate = [
          {
            file: {
              id: fileId,
              // ContentGrid will resolve these properties via data accessor or file resolution
              name: fileData.name || `File ${fileId}`,
              thumbnail_url: null, // Will be resolved
              content_thumbnails: null, // Will be resolved
              content_url: null, // Will be resolved - required for ContentGridFileProps but can be null
              content_type: null, // Will be resolved - required for ContentGridFileProps
              content_extension: null, // Will be resolved - required for ContentGridFileProps
              content_length: undefined, // Will be resolved
              permissions: undefined, // Will be resolved
              expires_at: undefined, // Will be resolved
              tags: undefined, // Will be resolved
            },
            type: 'file' as const,
          },
        ]
      }

      return [...acc, ...itemsToCreate]
    } catch (error) {
      // Skip invalid files
      return acc
    }
  }, [])

  return { items }
}
